import type { KeyboardEvent, ReactElement } from 'react';
import { useEffect, useRef } from 'react';
import styles from './QueueFullModal.module.css';

export interface QueueFullModalProps {
  onClose: () => void;
}

/**
 * Modal bloqueante exibido quando a `lead_queue` atinge o hard limit de
 * 10.000 registros (CA-09): a criação de um novo lead é bloqueada até a
 * fila drenar via sincronização automática. Só fecha pelo botão "OK" —
 * clique fora e Escape são desabilitados de propósito (Design > Modal de
 * Bloqueio). O formulário de lead por trás não é desabilitado: o operador
 * pode fechar e tentar de novo a qualquer momento.
 */
export function QueueFullModal({ onClose }: QueueFullModalProps): ReactElement {
  const okButtonRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<Element | null>(null);

  useEffect(() => {
    previousFocusRef.current = document.activeElement;
    okButtonRef.current?.focus();

    return () => {
      if (previousFocusRef.current instanceof HTMLElement) {
        previousFocusRef.current.focus();
      }
    };
  }, []);

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    // Bloqueante: Escape nunca fecha o modal (Design > Modal de Bloqueio) —
    // só o clique no botão "OK".
    if (event.key === 'Escape') event.preventDefault();
  }

  return (
    <div className={styles.overlay} onKeyDown={handleKeyDown}>
      <div
        className={styles.modal}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="queue-full-modal-title"
        aria-describedby="queue-full-modal-desc"
      >
        <p className={styles.icon} aria-hidden="true">
          🚫
        </p>
        <h2 id="queue-full-modal-title" className={styles.heading}>
          Fila Cheia
        </h2>
        <p id="queue-full-modal-desc" className={styles.message}>
          Limite de 10.000 leads sincronizados atingido. Aguarde a sincronização automática ou
          contate o suporte da agência.
        </p>
        <button ref={okButtonRef} type="button" className={styles.button} onClick={onClose}>
          OK
        </button>
      </div>
    </div>
  );
}
